import { Injectable, Optional } from '@angular/core';
import { AssistantMetaService } from './assistant-meta.service';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AssistantArea, IAssistantClientAction, IAssistantClientContext, IAssistantEntityRef, IAssistantStepInfo } from './assistant.models';

/**
 * Dataset con aree dell'assistente: dal server (assistant/meta, entity_types); questo elenco vale solo finché i
 * metadati non sono arrivati.
 */
export const ASSISTANT_ENTITY_TYPES = ['trip_packages', 'trip_categories', 'trip_destinations', 'trip_resources', 'products', 'actual_trips', 'meeting_points', 'trip_facilities'];

/**
 * Cosa sta guardando l'utente: route, schermata, entità (dataset detail/edit) e snapshot opzionale
 * pubblicato dai componenti di pagina. Lo snapshot è solo un indizio: il server ricarica sempre l'entità.
 */
@Injectable({
	providedIn: 'root'
})
export class AssistantContextService{

	public readonly entity = new BehaviorSubject<IAssistantEntityRef | null>(null);
	/** scheda in creazione (dataset/create/<tipo> con aree): tipo dell'entità non ancora salvata */
	public readonly newEntityType = new BehaviorSubject<string | null>(null);
	/** step aperto della scheda a step (common-step-view-container) */
	public readonly step = new BehaviorSubject<IAssistantStepInfo | null>(null);
	/** azioni lato client per la scheda aperta (fill_form, next_step, go_to_step): le esegue common-step-view-container */
	public readonly stepCommand = new Subject<IAssistantClientAction>();
	/** open_tab: scheda dello step aperto (la esegue lo step che ha le schede, es. Servizi del pacchetto) */
	public readonly tabCommand = new Subject<{ tab: string; lang: string | null }>();
	/** record appena creato dallo step 1 (salvataggio in creazione): la conversazione di creazione passa a lui */
	public readonly entityCreated = new Subject<IAssistantEntityRef>();
	/**
	 * Punti della UI dove l'assistente è abilitato (step Prezzi e Descrizioni del pacchetto, Servizi e prezzi, Listino).
	 * Il pulsante compare solo se almeno un componente in pagina si è ancorato: niente assistente altrove.
	 */
	public readonly anchored = new BehaviorSubject<boolean>(false);
	/** aree dei punti ancorati in pagina: il pulsante compare se almeno una è attiva nelle impostazioni del dominio */
	public readonly anchoredAreas = new BehaviorSubject<AssistantArea[]>([]);
	/** sezione aperta = area dell'ultimo punto ancorato (es. step Descrizioni → trip_packages_texts) */
	public readonly focusArea = new BehaviorSubject<AssistantArea | null>(null);
	/** nuovo punto ancorato (es. si apre il Listino): lo stato server va riletto, potrebbe essere cambiato altrove */
	public readonly anchorAdded = new Subject<string>();

	/** key → area */
	private anchors = new Map<string, AssistantArea>();

	private screen: string | null = null;
	/** valore o funzione valutata a ogni messaggio (snapshot sempre aggiornato senza subscribe) */
	private snapshots = new Map<string, any | (() => any)>();

	private entityTypes: string[] = ASSISTANT_ENTITY_TYPES;

	constructor(private router: Router, @Optional() metaService?: AssistantMetaService){
		metaService?.meta.pipe(filter(Boolean)).subscribe(meta => {
			this.entityTypes = meta.entity_types;
			this.update();
		});
		this.update();
		this.router.events
		.pipe(filter(event => event instanceof NavigationEnd))
		.subscribe(() => this.update());
	}

	/**
	 * Pubblica cosa mostra un componente (es. step Prezzi). Rimuovere con clearSnapshot all'onDestroy.
	 */
	setSnapshot(key: string, data: any | (() => any)): void{
		this.snapshots.set(key, data);
	}

	clearSnapshot(key: string): void{
		this.snapshots.delete(key);
	}

	/**
	 * Abilita l'assistente finché il componente resta in pagina, se la sua area è attiva per il dominio;
	 * rilasciare con release() all'onDestroy.
	 */
	anchor(key: string, area: AssistantArea): void{
		// in coda alla Map: l'ultimo ancorato decide la sezione aperta
		this.anchors.delete(key);
		this.anchors.set(key, area);
		this.publishAreas();
		if(!this.anchored.value) this.anchored.next(true);
		this.anchorAdded.next(key);
	}

	release(key: string): void{
		if(!this.anchors.delete(key)) return;
		this.publishAreas();
		if(!this.anchors.size && this.anchored.value) this.anchored.next(false);
	}

	private publishAreas(): void{
		const focus = Array.from(this.anchors.values()).pop() ?? null;
		if(focus !== this.focusArea.value) this.focusArea.next(focus);
		const areas = Array.from(new Set(this.anchors.values())).sort();
		const previous = this.anchoredAreas.value;
		if(areas.length !== previous.length || areas.some((area, index) => area !== previous[index])) this.anchoredAreas.next(areas);
	}

	setStep(step: IAssistantStepInfo | null): void{
		const previous = this.step.value;
		if(previous?.index === step?.index && previous?.title === step?.title && previous?.next_title === step?.next_title && previous?.count === step?.count
			&& JSON.stringify(previous?.steps ?? null) === JSON.stringify(step?.steps ?? null)) return;
		this.step.next(step);
	}

	current(): IAssistantClientContext{
		const snapshot = this.snapshots.size
			? Object.fromEntries(Array.from(this.snapshots.entries()).map(([key, value]) => [key, typeof value === 'function' ? value() : value]))
			: undefined;
		return {
			route: this.router.url,
			screen: this.screen || undefined,
			area: this.focusArea.value || undefined,
			entity: this.entity.value ?? (this.newEntityType.value ? { type: this.newEntityType.value } : null),
			snapshot,
			locale: 'it',
			timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
			...(this.step.value ? { step: this.step.value } : {})
		};
	}

	private update(): void{
		const { entity, screen, newEntityType } = AssistantContextService.parse(this.router.routerState.snapshot.root, this.entityTypes);
		this.screen = screen;
		// prima l'entità, poi la scheda in creazione: passando da creazione a modifica la conversazione non deve
		// transitare per "nessuna entità" (AssistantService la sposta sul record appena salvato)
		const previous = this.entity.value;
		if(previous?.type !== entity?.type || previous?.id !== entity?.id) this.entity.next(entity);
		if(newEntityType !== this.newEntityType.value) this.newEntityType.next(newEntityType);
	}

	/**
	 * Route più profonda con params.datasetCode + params.recordId → entità se il tipo è supportato; senza recordId in
	 * creazione → newEntityType (il server attiva solo le aree di creazione di quel tipo, se ne ha).
	 */
	static parse(root: ActivatedRouteSnapshot, entityTypes: string[] = ASSISTANT_ENTITY_TYPES): { entity: IAssistantEntityRef | null; screen: string | null; newEntityType: string | null }{
		let route = root;
		while(route.firstChild) route = route.firstChild;

		const datasetCode = route.params?.datasetCode;
		const recordId = route.params?.recordId;
		const action = route.data?.action;
		const screen = datasetCode ? ['dataset', action, datasetCode].filter(Boolean).join('.') : (route.routeConfig?.path || null);

		if(datasetCode && recordId && entityTypes.includes(datasetCode) && ['detail', 'edit'].includes(action)){
			return { entity: { type: datasetCode, id: String(recordId) }, screen, newEntityType: null };
		}
		return { entity: null, screen, newEntityType: datasetCode && !recordId && action === 'create' && entityTypes.includes(datasetCode) ? datasetCode : null };
	}
}
